import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Play, Pause, ChevronLeft, ChevronRight, X, Sparkles, ArrowRight, RotateCw,
  CheckCircle2, Mail, ZoomIn, Loader2, Send,
} from "lucide-react";
import api from "@/lib/api";
import { NovatusLogo } from "@/components/NovatusLogo";
import { Button } from "@/components/ui/button";

const SLIDE_MS = 7000;

// 15-Step Demo-Skript für „Lena Vertrieb"
// Jede Slide kombiniert echten App-Screenshot mit den Highlight-Bullets aus
// der ehemaligen App-Tour, damit der Demo-Rundgang inhaltlich dicht ist.
const SLIDES = [
  {
    img: "/demo/step-01-login.jpeg",
    title: "Schritt 1 · Anmelden",
    body: "Lena meldet sich per E-Mail oder Mobilnummer in unter 5 Sekunden an — keine Passwörter, nur ein OTP per E-Mail.",
    highlight: "Sicher · Schnell · Mobil",
    bullets: [
      "Erst-Login & Registrierung im selben Flow",
      "Auto-Aktivierung bei plausiblen Daten",
      'Profil später jederzeit unter „Mein Profil"',
    ],
  },
  {
    img: "/demo/step-02-dashboard.jpeg",
    title: "Schritt 2 · Vertriebs-Dashboard",
    body: "Sofort sichtbar: aktuelle Projekte, KPIs, Gewinnquote. Ein Klick und das nächste Projekt ist offen.",
    highlight: "100 % Übersicht",
    bullets: [
      "Projekte gesamt, in Bearbeitung, gewonnen",
      "Tester-Handbuch & Akademie ein Klick weg",
      "Aktuelle Projekte mit Status-Badges",
    ],
  },
  {
    img: "/demo/step-03-projects.jpeg",
    title: "Schritt 3 · Projektliste",
    body: 'Alle Projekte mit Status-Badge und Filter — von „Erstkontakt" bis „Auftrag unterschrieben". Suchen, sortieren, durchstarten.',
    highlight: "Pipeline auf einen Blick",
    bullets: [
      "Stage-Filter & Volltextsuche",
      "Stale-Indikator für ältere Leads",
      "Pro Projekt eindeutige Nummer (P-…)",
    ],
  },
  {
    img: "/demo/step-04-customer.jpeg",
    title: "Schritt 4 · Kundendaten",
    body: "Anrede, Adresse, Telefon, E-Mail — Pflichtfelder mit Validierung, Postleitzahl-Autofill, vollständig DSGVO-konform.",
    highlight: "DIN-konform & DSGVO",
    bullets: [
      "Anrede: Herr · Frau · Familie · Divers",
      "PLZ-Auto-Complete · Ort wird ergänzt",
      "Sprach-Diktat für Notizen vor Ort",
    ],
  },
  {
    img: "/demo/step-05-media.jpeg",
    title: "Schritt 5 · Fotos & Videos",
    body: "Vor Ort einfach mit Kamera knipsen oder Skizze zeichnen — alles offline möglich, sync wenn Netz da ist.",
    highlight: "Offline-fähig",
    bullets: [
      "Fotos · Videos · handgezeichnete Skizzen",
      "Hintergrund-Sync sobald wieder online",
      "Annotation direkt im Browser möglich",
    ],
  },
  {
    img: "/demo/step-06-tech.jpeg",
    title: "Schritt 6 · Technischer Check",
    body: "Heizraum, Schornstein, Hydraulik, Elektrik — strukturierte Checkliste mit KI-Foto-Analyse, die Geräte selbstständig erkennt.",
    highlight: "KI-gestützte Aufnahme",
    bullets: [
      "Gemini Vision identifiziert Heizungstyp",
      "Hersteller & Baujahr werden vorausgefüllt",
      "Empfohlene WP-Größe direkt vorgeschlagen",
    ],
  },
  {
    img: "/demo/step-07-consumption.jpeg",
    title: "Schritt 7 · Verbrauch & Heizlast",
    body: "Echte Verbrauchswerte rein → Heizlast nach DIN EN 12831 raus. Keine Excel-Tabellen mehr. Empfohlene WP-Größe direkt.",
    highlight: "DIN EN 12831",
    bullets: [
      "Heizöl, Gas, Strom — flexible Quellen",
      "Heizlast-Berechnung über Vor-Ort-Daten",
      "Sofort-Empfehlung passender HEIKO-Größe",
    ],
  },
  {
    img: "/demo/step-08-offer.jpeg",
    title: "Schritt 8 · Angebot konfigurieren",
    body: "HEIKO-Paket auswählen, Komponenten swappen, KfW-Förderung einrechnen — Preis aktualisiert sich live.",
    highlight: "Live-Kalkulation",
    bullets: [
      "Pakete: Günstig · Komfort · Premium",
      "BAFA & KfW458 automatisch eingerechnet",
      "Komponenten ein- oder ausschalten",
    ],
  },
  {
    img: "/demo/step-09-packages.jpeg",
    title: "Schritt 9 · Pakete & Komponenten",
    body: "Festpreis-Paket mit allen Standard-Komponenten. Brauchwasserspeicher, Pufferspeicher, Speicher-Größe — alles modular.",
    highlight: "10 Komponenten · 1 Preis",
    bullets: [
      "Standardkomponenten direkt enthalten",
      "Optionen: Speicher-Größe, Premium-Upgrades",
      "Preis-Diff zu Standard sofort sichtbar",
    ],
  },
  {
    img: "/demo/step-10-pdf-actions.jpeg",
    title: "Schritt 10 · PDF auf Knopfdruck",
    body: "Vollständiges Angebot mit Wirtschaftlichkeitsrechner, 15-Jahres-Amortisation, CO₂-Bilanz und BEHG-Pfad — als PDF generiert.",
    highlight: "15-Jahres-Wirtschaftlichkeit",
    bullets: [
      "Recharts-Liniendiagramm im PDF",
      "Amortisationspunkt live markiert",
      "BAFA & KfW-Förderung integriert",
    ],
  },
  {
    img: "/demo/step-11-send-email.jpeg",
    title: "Schritt 11 · Per E-Mail versenden",
    body: "Ein Klick → Kunde bekommt personalisierte E-Mail mit Angebots-PDF, Begleittext und Status-Tracker-Link. Stage geht automatisch weiter.",
    highlight: "Kundenkommunikation in 5 Sekunden",
    bullets: [
      "Personalisierte Anrede aus Kundendaten",
      "Direktversand per United-Domains SMTP",
      "Öffentlicher Tracking-Link für Kunden",
    ],
  },
  {
    img: "/demo/step-12-signature.jpeg",
    title: "Schritt 12 · Digitale Unterschrift",
    body: "Beim Kunden vor Ort? Direkt auf iPhone/iPad unterschreiben. Signierte PDF geht automatisch per E-Mail an den Kunden.",
    highlight: "Abschluss am Küchentisch",
    bullets: [
      "Touch-Canvas für die Unterschrift",
      "Auftragsbestätigung automatisch erzeugt",
      'Stage „Auftrag unterschrieben" sofort aktiv',
    ],
  },
  {
    img: "/demo/step-13-status.jpeg",
    title: "Schritt 13 · Auftragsstatus-Tracker",
    body: "Pipeline-Übersicht von Lead bis Abschlussrechnung. Kunde sieht über öffentlichen Link den eigenen Projektfortschritt.",
    highlight: "Transparenz für Kunden",
    bullets: [
      "10 Stages vom Erstkontakt bis Abnahme",
      "Anzahlung & Schlussrechnung automatisiert",
      "USt.-konforme PDF-Rechnungen",
    ],
  },
  {
    img: "/demo/step-14-reminder.jpeg",
    title: "Schritt 14 · Wiedervorlage",
    body: "Aufgabe anlegen, Fälligkeit setzen, Push-Erinnerung wählen — Lena vergisst kein Follow-up mehr.",
    highlight: "Nie wieder Kunden verlieren",
    bullets: [
      "Push-Erinnerung 1h / 1d / 1 Woche vorher",
      "Termine pro Projekt verknüpft",
      "Bald: .ics-Sync mit Outlook & Google",
    ],
  },
  {
    img: "/demo/step-15-tasks.jpeg",
    title: "Schritt 15 · Alles unter Kontrolle",
    body: "Tägliche Aufgabenliste pro Projekt, Prioritäten, Erledigt-Status. Lena schließt den Tag mit klarem Kopf ab.",
    highlight: "Ein Tool. Ein Vertrieb. Maximaler Output.",
    bullets: [
      "Aufgaben nach Aktiv / Erledigt gefiltert",
      "Prioritäten Niedrig · Normal · Hoch",
      "Offline-fähig als installierbare PWA",
    ],
  },
];

// ─── Fullscreen lightbox with custom pinch-zoom + pan ──────────────────────
// Native browser pinch-zoom is disabled by the PWA viewport meta
// (`user-scalable=no`), so we implement a touch/mouse-based zoom controller
// using CSS transforms. Supports: 2-finger pinch, drag-to-pan, double-tap to
// zoom in / reset, plus +/- buttons for desktop.
function ZoomLightbox({ slide, idx, total, onClose, onPrev, onNext, canPrev, canNext }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const stage = useRef(null);
  // Refs to avoid re-creating handlers
  const state = useRef({
    pinchStartDist: 0,
    pinchStartScale: 1,
    panStartX: 0,
    panStartY: 0,
    panStartTx: 0,
    panStartTy: 0,
    isPanning: false,
    lastTapAt: 0,
  });

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;

  const clampPan = useCallback((nextScale, nextTx, nextTy) => {
    const el = stage.current;
    if (!el) return { x: nextTx, y: nextTy };
    const w = el.clientWidth;
    const h = el.clientHeight;
    // Allow panning only inside the extra space created by zoom
    const overflowX = (w * (nextScale - 1)) / 2;
    const overflowY = (h * (nextScale - 1)) / 2;
    const cx = Math.min(overflowX, Math.max(-overflowX, nextTx));
    const cy = Math.min(overflowY, Math.max(-overflowY, nextTy));
    return { x: cx, y: cy };
  }, []);

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // Reset on slide change
  useEffect(() => { reset(); }, [idx, reset]);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = e.touches;
      state.current.pinchStartDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      state.current.pinchStartScale = scale;
    } else if (e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      const t = e.touches[0];
      state.current.isPanning = true;
      state.current.panStartX = t.clientX;
      state.current.panStartY = t.clientY;
      state.current.panStartTx = tx;
      state.current.panStartTy = ty;
    } else if (e.touches.length === 1) {
      // double-tap detection (zoom-in / out)
      const now = Date.now();
      if (now - state.current.lastTapAt < 280) {
        if (scale > 1) {
          reset();
        } else {
          setScale(2.5);
        }
      }
      state.current.lastTapAt = now;
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && state.current.pinchStartDist > 0) {
      e.preventDefault();
      const [a, b] = e.touches;
      const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      let next = state.current.pinchStartScale * (dist / state.current.pinchStartDist);
      next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
      setScale(next);
      if (next === 1) { setTx(0); setTy(0); }
    } else if (e.touches.length === 1 && state.current.isPanning) {
      e.preventDefault();
      const t = e.touches[0];
      const dx = t.clientX - state.current.panStartX;
      const dy = t.clientY - state.current.panStartY;
      const { x, y } = clampPan(scale, state.current.panStartTx + dx, state.current.panStartTy + dy);
      setTx(x); setTy(y);
    }
  };

  const onTouchEnd = () => {
    state.current.pinchStartDist = 0;
    state.current.isPanning = false;
  };

  // Mouse wheel zoom on desktop
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return; // require modifier so page scroll still works
    e.preventDefault();
    const delta = -e.deltaY * 0.01;
    let next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + delta));
    setScale(next);
    if (next === 1) { setTx(0); setTy(0); }
  };

  const zoomBtn = (delta) => {
    let next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + delta));
    setScale(next);
    if (next === 1) { setTx(0); setTy(0); }
  };

  // Disable native page scroll while lightbox open
  useEffect(() => {
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = "hidden";
    return () => { body.style.overflow = prev; };
  }, []);

  return (
    <div
      data-testid="demo-zoom-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col select-none"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-[12px] uppercase tracking-widest font-bold text-[#00E575] truncate pr-3">
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {slide.title}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="demo-zoom-out"
            onClick={() => zoomBtn(-0.5)}
            disabled={scale <= MIN_SCALE}
            className="w-10 h-10 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 flex items-center justify-center font-bold text-lg"
            aria-label="Verkleinern"
          >−</button>
          <span className="px-2 text-[12px] font-mono text-zinc-300 min-w-[3rem] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            data-testid="demo-zoom-in"
            onClick={() => zoomBtn(0.5)}
            disabled={scale >= MAX_SCALE}
            className="w-10 h-10 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 flex items-center justify-center font-bold text-lg"
            aria-label="Vergrößern"
          >+</button>
          <button
            data-testid="demo-zoom-close"
            onClick={onClose}
            className="ml-1 w-10 h-10 rounded-xl border border-zinc-700 hover:border-zinc-500 flex items-center justify-center"
            aria-label="Schließen"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Image stage with custom zoom/pan */}
      <div
        ref={stage}
        className="flex-1 overflow-hidden flex items-center justify-center"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        style={{ touchAction: "none" }}
      >
        <img
          src={slide.img}
          alt={slide.title}
          data-testid="demo-zoom-image"
          draggable={false}
          className="block max-h-full max-w-full transition-transform duration-75 will-change-transform"
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: "center center",
          }}
        />
      </div>

      {/* Footer: nav + hint */}
      <div className="px-4 pt-2 pb-3 flex items-center gap-2">
        <button
          data-testid="demo-zoom-prev"
          onClick={onPrev}
          disabled={!canPrev}
          className="h-11 w-11 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          aria-label="Vorherige Slide"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 text-center text-[12px] text-zinc-400 leading-snug px-2">
          <span className="block">2-Finger zoomen · Doppeltipp = Zoom an/aus · Ziehen = verschieben</span>
          <span className="block mt-0.5 text-zinc-500">
            Slide {idx + 1} von {total}
          </span>
        </div>
        <button
          data-testid="demo-zoom-next"
          onClick={onNext}
          disabled={!canNext}
          className="h-11 w-11 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          aria-label="Nächste Slide"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ─── Contact Modal (PWA-safe fallback for mailto: that fails in standalone mode)
// ─── Contact Modal — real lead-capture form, sends mail to internal recipient
function ContactModal({ onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
    form.phone.trim().length >= 4;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/demo-contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Anfrage konnte nicht gesendet werden. Bitte später erneut versuchen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="demo-contact-modal"
      className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-[#0D0D10] border-2 border-amber-400/40 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-amber-400" />
            <h3 className="font-display text-lg font-bold text-zinc-100">Termin vereinbaren</h3>
          </div>
          <button
            data-testid="demo-contact-close"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-zinc-700 hover:border-zinc-500 flex items-center justify-center"
            aria-label="Schließen"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="px-5 py-8 text-center" data-testid="demo-contact-success">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#00E575]/15 flex items-center justify-center">
              <CheckCircle2 className="text-[#00E575]" size={28} />
            </div>
            <h4 className="font-display text-xl font-bold text-zinc-100 mb-2">
              Vielen Dank!
            </h4>
            <p className="text-sm text-zinc-300 leading-relaxed mb-5">
              Ihre Anfrage ist bei uns eingegangen. Wir melden uns innerhalb der nächsten
              24 Stunden bei Ihnen unter <strong className="text-amber-300">{form.phone}</strong>{" "}
              oder per E-Mail an <strong className="text-amber-300">{form.email}</strong>.
            </p>
            <button
              data-testid="demo-contact-done"
              onClick={onClose}
              className="h-11 px-6 rounded-xl bg-[#00E575] text-[#09090B] font-bold hover:bg-[#00C964] active:scale-95 transition-all"
            >
              Schließen
            </button>
          </div>
        ) : (
          <form className="px-5 py-5 overflow-y-auto" onSubmit={submit}>
            <p className="text-sm text-zinc-300 leading-relaxed mb-4">
              Wir rufen Sie umgehend zurück — bitte einmal kurz Ihre Kontaktdaten:
            </p>

            <label className="block text-[12px] font-bold uppercase tracking-widest text-amber-300 mb-1.5">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              data-testid="demo-contact-name"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Vor- und Nachname"
              className="w-full h-11 px-3 mb-3 bg-[#09090B] border border-zinc-700 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-400"
            />

            <label className="block text-[12px] font-bold uppercase tracking-widest text-amber-300 mb-1.5">
              E-Mail <span className="text-red-400">*</span>
            </label>
            <input
              data-testid="demo-contact-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="name@firma.de"
              className="w-full h-11 px-3 mb-3 bg-[#09090B] border border-zinc-700 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-400"
            />

            <label className="block text-[12px] font-bold uppercase tracking-widest text-amber-300 mb-1.5">
              Telefon <span className="text-red-400">*</span>
            </label>
            <input
              data-testid="demo-contact-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+49 171 1234567"
              className="w-full h-11 px-3 mb-3 bg-[#09090B] border border-zinc-700 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-400"
            />

            <label className="block text-[12px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">
              Nachricht (optional)
            </label>
            <textarea
              data-testid="demo-contact-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Wann passt Ihnen ein Anruf? Anmerkungen?"
              maxLength={2000}
              className="w-full h-24 px-3 py-2 mb-2 bg-[#09090B] border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-400 resize-none"
            />

            {/* Honeypot — visually hidden, but reachable for naive bots */}
            <input
              tabIndex={-1}
              autoComplete="off"
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
              aria-hidden="true"
            />

            {error && (
              <p
                data-testid="demo-contact-error"
                className="mb-3 px-3 py-2 rounded-lg bg-red-500/15 border border-red-500/40 text-red-200 text-sm"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="demo-contact-submit"
              disabled={!canSubmit || submitting}
              className="mt-2 w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed text-[#1a0f00] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />}
              {submitting ? "Wird gesendet…" : "Anfrage senden"}
            </button>

            <p className="mt-3 text-xs font-medium text-zinc-300 text-center leading-snug">
              Wir verwenden Ihre Daten ausschließlich zur Terminanfrage. Keine Newsletter, keine
              Weitergabe an Dritte. <a href="/legal/datenschutz" className="underline font-semibold text-zinc-100 hover:text-[#00E575]">Datenschutz</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default function DemoTour() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();
  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;

  const go = useCallback((delta) => {
    setIdx((i) => {
      const n = i + delta;
      if (n < 0) return 0;
      if (n >= SLIDES.length) return SLIDES.length - 1;
      return n;
    });
    setProgress(0);
  }, []);

  useEffect(() => {
    if (paused || isLast) return;
    setProgress(0);
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(100, (elapsed / SLIDE_MS) * 100);
      setProgress(p);
      if (elapsed >= SLIDE_MS) {
        clearInterval(interval);
        setIdx((i) => (i < SLIDES.length - 1 ? i + 1 : i));
      }
    }, 80);
    timer.current = interval;
    return () => clearInterval(interval);
  }, [idx, paused, isLast]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
      else if (e.key === "Escape") {
        if (zoomOpen) setZoomOpen(false);
        else navigate("/login");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, navigate, zoomOpen]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-[#0D0D10]">
        <Link to="/login" className="flex items-center gap-2" data-testid="demo-back-login">
          <NovatusLogo size="sm" />
        </Link>
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#00E575]" />
          <span className="text-[12px] uppercase tracking-widest font-bold text-[#00E575]">
            Demo-Rundgang · Vertrieb
          </span>
        </div>
        <button
          data-testid="demo-close"
          onClick={() => navigate("/login")}
          className="w-9 h-9 rounded-lg border border-zinc-800 hover:border-zinc-600 flex items-center justify-center"
        >
          <X size={16} />
        </button>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-zinc-900 overflow-hidden">
        <div
          className="h-full bg-[#00E575] transition-all duration-100"
          style={{ width: `${((idx + (isLast ? 1 : progress / 100)) / SLIDES.length) * 100}%` }}
        />
      </div>

      {/* Main: image + caption */}
      <main className="flex-1 flex flex-col md:flex-row items-start max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 gap-6 md:gap-8 lg:gap-10">
        {/* Screenshot — tap to open in pinch-zoom lightbox */}
        <div className="w-full md:flex-1 md:max-w-[55%] lg:max-w-[60%] mx-auto flex justify-center">
          <button
            type="button"
            data-testid="demo-zoom-open"
            onClick={() => { setZoomOpen(true); setPaused(true); }}
            className="group block relative rounded-3xl overflow-hidden border-2 border-zinc-700 hover:border-[#00E575]/50 shadow-[0_20px_60px_rgba(0,229,117,0.18)] bg-zinc-900 max-h-[78vh] active:scale-[0.99] transition-all"
            aria-label="Bild vergrößern"
          >
            <img
              src={slide.img}
              alt={slide.title}
              data-testid={`demo-slide-img-${idx + 1}`}
              className="block max-h-[78vh] max-w-full w-auto h-auto object-contain"
              loading="eager"
              draggable={false}
            />
            <span className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md text-[12px] font-bold uppercase tracking-widest text-[#00E575] border border-[#00E575]/40">
              <ZoomIn size={12} /> Antippen zum Zoomen
            </span>
          </button>
        </div>

        {/* Caption + controls */}
        <div className="w-full md:flex-1 md:max-w-[45%] lg:max-w-[40%] flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="font-mono text-xs text-zinc-400 font-medium">
              {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
            </div>
            <div className="flex-1 h-px bg-zinc-800" />
            <div className="px-2 py-0.5 rounded-full bg-[#00E575]/15 border border-[#00E575]/30 text-[12px] font-bold uppercase tracking-widest text-[#00E575]">
              {slide.highlight}
            </div>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-3" data-testid="demo-slide-title">
            {slide.title}
          </h2>
          <p className="text-base text-zinc-300 leading-relaxed mb-4" data-testid="demo-slide-body">
            {slide.body}
          </p>

          {/* Bullet highlights */}
          {slide.bullets && slide.bullets.length > 0 && (
            <ul className="space-y-2 mb-5" data-testid="demo-slide-bullets">
              {slide.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                  <CheckCircle2 size={15} className="text-[#00E575] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTA on last slide */}
          {isLast && (
            <div className="mt-2 p-5 rounded-2xl border border-[#00E575]/40 bg-[#00E575]/10">
              <h3 className="font-display text-lg font-bold mb-1.5">Selbst ausprobieren?</h3>
              <p className="text-sm text-zinc-300 mb-4">
                Klick auf eine der Quick-Login-Karten auf der Login-Seite und erlebe den Workflow live —
                oder vereinbare gleich einen persönlichen Termin mit uns.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/login"
                  data-testid="demo-cta-start"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold hover:bg-[#00C964] active:scale-95 transition-all"
                >
                  Jetzt selbst testen <ArrowRight size={18} />
                </Link>
                <button
                  type="button"
                  data-testid="demo-cta-contact"
                  onClick={() => setContactOpen(true)}
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-xl border-2 border-amber-400/60 bg-amber-500/10 text-amber-300 font-bold hover:bg-amber-500/20 hover:border-amber-400 active:scale-95 transition-all cursor-pointer"
                >
                  <Mail size={18} /> Termin vereinbaren
                </button>
              </div>
            </div>
          )}

          {/* Bottom controls */}
          <div className="mt-auto pt-6 flex items-center gap-2">
            <button
              data-testid="demo-prev"
              onClick={() => go(-1)}
              disabled={idx === 0}
              className="h-12 w-12 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="demo-pause"
              onClick={() => setPaused((p) => !p)}
              disabled={isLast}
              className="h-12 px-4 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {paused ? <Play size={16} /> : <Pause size={16} />}
              <span className="text-sm font-medium">{paused ? "Weiter" : "Pause"}</span>
            </button>
            <button
              data-testid="demo-next"
              onClick={() => go(1)}
              disabled={isLast}
              className="h-12 w-12 rounded-xl border border-zinc-700 hover:border-[#00E575]/60 hover:text-[#00E575] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <ChevronRight size={18} />
            </button>
            {isLast && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIdx(0); setProgress(0); }}
                data-testid="demo-restart"
                className="ml-2 text-zinc-400 hover:text-zinc-100"
              >
                <RotateCw size={14} className="mr-1.5" /> Von vorn
              </Button>
            )}
            <div className="ml-auto text-[12px] text-zinc-300 hidden sm:block">
              ← → zum Navigieren · Leertaste = Pause
            </div>
          </div>

          {/* Step dots */}
          <div className="mt-4 flex flex-wrap gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`demo-dot-${i + 1}`}
                onClick={() => { setIdx(i); setProgress(0); }}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx
                    ? "w-8 bg-[#00E575]"
                    : i < idx
                    ? "w-3 bg-[#00E575]/40 hover:bg-[#00E575]/70"
                    : "w-3 bg-zinc-700 hover:bg-zinc-600"
                }`}
                aria-label={`Zu Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </main>

      {/* Fullscreen Lightbox with custom pinch-zoom (works inside PWA where native scale is disabled) */}
      {zoomOpen && (
        <ZoomLightbox
          slide={slide}
          idx={idx}
          total={SLIDES.length}
          onClose={() => setZoomOpen(false)}
          onPrev={() => go(-1)}
          onNext={() => go(1)}
          canPrev={idx > 0}
          canNext={idx < SLIDES.length - 1}
        />
      )}

      {/* Contact-Modal (PWA-safe fallback when mailto: doesn't open the mail app) */}
      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
    </div>
  );
}
